/* Next */
import { useRouter } from 'next/router'

/* React */
import { useEffect, useRef } from 'react'

/* Redux */
import Redux from './Navigation.Redux'

/* Components */
import NavigationPrimary from './NavigationPrimary'
import NavigationMenuPages from './NavigationMenuPages'
import NavigationFilterButton from './NavigationFilterButton'
import FilterTree from './NavigationFilterTree'
import ModernNavigation from './ModernNavigation'
import config from 'src/config'

/* Types */
import { tMenu, item } from 'src/shared/frontendTypes/navigation'

/* Hooks */
import useNavigationPaths from 'src/layout/navigationPaths'

interface Props { placement: 'layout' | 'drawer' }

const Navigation: React.FC<Props> = ({ placement }) => {
  const paths = useNavigationPaths()

  const { 
    displayMenuId, setDisplayMenuId,
    displayFilter, setDisplayFilter,
    lastScrollTops, saveScrollTop,
  } = Redux()

  const router = useRouter()
  const restoreScrollOnRouteComplete = useRef(false)

  const activeMenuId = router.query.mid ? parseInt(router.query.mid as string) : null
  const activeBrandId = router.query.bid ? parseInt(router.query.bid as string) : null

  /* ------------------------------------------------------ */
  // Beim Start im Menü zu aktiven Menüpunkt springen

  const preSelectMenu = () => {
    if (activeMenuId !== null || activeBrandId !== null) {
      const getPath = (
        paths: tMenu,
        mid: number | null,
        bid: number | null,
      ): item | null => {
        for (const path of paths) {
          const { menueId, brand, children } = path;
          if (mid && !bid && menueId === mid) return path;
          if (bid && !mid && brand === bid) return path;
          if (mid && bid && menueId === mid && brand === bid) return path;
          if (children) {
            const subPath = getPath(children, mid, bid);
            if (subPath) return subPath;
          }
        }
        return null;
      };
  
      const activeItem = getPath(paths, activeMenuId, activeBrandId);
      if (!activeItem) return;
  
      const newDisplayMenuId =
        activeItem.children && activeItem.children.length > 0
          ? activeItem.id
          : activeItem.parent || 0;

      setDisplayMenuId(newDisplayMenuId);
    }
  };

  /* ------------------------------------------------------ */
  // Bei wechsel zu normalen Seiten das Menü zurücksetzen

  useEffect(() => {

    if (!router.isReady) return

    const normalizeScrollUrl = (value: string) => {
      if (typeof window === 'undefined') return value.split('#')[0]

      try {
        const url = new URL(value, window.location.origin)
        return `${url.pathname}${url.search}`
      } catch {
        return value.split('#')[0]
      }
    }

    const previousScrollRestoration = typeof window !== 'undefined'
      ? window.history.scrollRestoration
      : undefined

    if (
      typeof window !== 'undefined'
      && 'scrollRestoration' in window.history
    ) {
      window.history.scrollRestoration = 'manual'
    }

    router.beforePopState(() => {
      restoreScrollOnRouteComplete.current = true
      return true
    })

    const resetMenu = (url: string) => {

      // Markenseite
      if (url.match(/^\/brands$/)) {
        setDisplayFilter(false)
      }

      // Alle Seiten außer Filter- und Produktseiten Hauptmenü
      else if (!url.match(/^(\/(m|b|p)\/.*)$/)) {
        if (displayMenuId !== 0) setDisplayMenuId(0)
        if (displayFilter === true) setDisplayFilter(false)
      }

      // Alle Seiten außer Filter-Seiten keinen Filter anzeigen
      else if (!url.match(/^(\/(m|b)\/.*)$/)) {
        if (displayFilter === true) setDisplayFilter(false)
      }

      // Auf Marken-Seiten das Marken-Menü öffnen
      else if (url.match(/^\/b\/.*$/)) {
        preSelectMenu()
        if (
          !url.match(/^\/b.+\/[\d]+\/m\/+.*[\d]$/)
          && displayFilter === true
        ) setDisplayFilter(false)
      }

    }

    const onRouteChangeStart = () => {
      saveScrollTop(normalizeScrollUrl(router.asPath), window.scrollY)
    }

    const onRouteChangeComplete = (url: string) => {
      resetMenu(url)

      const scrollUrl = normalizeScrollUrl(url)

      if (restoreScrollOnRouteComplete.current) {
        restoreScrollOnRouteComplete.current = false
        const lastScrollTop = lastScrollTops.find(x => x.url === scrollUrl)

        window.requestAnimationFrame(() => {
          window.scrollTo(0, lastScrollTop?.y || 0)
        })

        return
      }

      if (!url.includes('#')) {
        window.requestAnimationFrame(() => {
          window.scrollTo(0, 0)
        })
      }
    }

    router.events.on('routeChangeStart', onRouteChangeStart)
    router.events.on('routeChangeComplete', onRouteChangeComplete)

    return () => {
      router.events.off('routeChangeStart', onRouteChangeStart)
      router.events.off('routeChangeComplete', onRouteChangeComplete)
      router.beforePopState(() => true)
      if (
        previousScrollRestoration !== undefined
        && typeof window !== 'undefined'
        && 'scrollRestoration' in window.history
      ) {
        window.history.scrollRestoration = previousScrollRestoration
      }
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    displayMenuId, 
    displayFilter, 
    router.isReady, router.events, router.query, router.asPath,
  ])

  /* ------------------------------------------------------ */
  
  useEffect(() => {
    if (!router.isReady) return
    preSelectMenu()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router.isReady, activeMenuId, activeBrandId, paths])

  /* ------------------------------------------------------ */

  if (placement === 'drawer' && config.mobileMenuVariant === 'modern') {
    return <ModernNavigation {...{ paths, activeMenuId, activeBrandId }} />
  }

  return <>

    { !displayFilter && [
      '/suche',
      '/m/[m_alias]/[mid]',
      '/b/[b_alias]/[bid]',
      '/b/[b_alias]/[bid]/m/[m_alias]/[mid]',
    ].includes(router.pathname) &&
      <NavigationFilterButton />
    }

    { !displayFilter && (placement === 'drawer' || displayMenuId === 0) &&
      <NavigationPrimary />
    }

    <NavigationMenuPages 
      {...{paths, activeMenuId, activeBrandId}}
    />

    {displayFilter &&
      <FilterTree {...{placement}} />
    }

  </>

}

export default Navigation
